/**
 * Module dependencies.
 */

import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from 'app/components/ui/button';
import { Badge } from 'app/components/ui/badge';
import { Input } from 'app/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'app/components/ui/select';
import { useToast } from 'app/components/providers/toast-provider/toast-provider';
import { cn } from 'app/lib/utils';
import { CheckCircle2, Download, FolderOpen, Loader2, Minimize2, Play, X, XCircle } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from 'app/components/ui/dialog';
import {
  DownloadItem,
  useBacktestDownloadStatus,
  useBacktestRunStatus,
  useCancelBacktestDownload,
  useCancelBacktestRun,
  useCryptoBotBacktestData,
  useCryptoBotPairFiles,
  useStartBacktestDownload,
  useStartBacktestRun
} from 'app/hooks/requests/crypto-bot/use-crypto-bot';

/**
 * A thin progress bar. `tone` colours the fill: active (orange), done (green),
 * error (red) or the default primary.
 */

function Bar({ value, tone }: { value: number; tone?: 'active' | 'done' | 'error' }): JSX.Element {
  return (
    <div className={'h-2 w-full overflow-hidden rounded-full bg-muted'}>
      <div
        className={cn(
          'h-full rounded-full transition-all duration-300',
          tone === 'done' && 'bg-emerald-500',
          tone === 'active' && 'bg-amber-500',
          tone === 'error' && 'bg-destructive',
          !tone && 'bg-primary'
        )}
        style={{ width: `${Math.min(100, Math.max(0, value))}%` }}
      />
    </div>
  );
}

/**
 * A tri-state download bar in a single track: green = completed, amber =
 * currently downloading, grey = queued. e.g. 3 done + 5 active of 37 reads
 * |VVVAAAAA............|. Fully green when done, red on error.
 */

function DownloadBar({
  status,
  donePct,
  activePct,
  rounded = 'full'
}: {
  status: string;
  donePct: number;
  activePct: number;
  rounded?: 'full' | 'sm';
}): JSX.Element {
  const radius = rounded === 'full' ? 'rounded-full' : 'rounded-sm';

  if (status === 'error') {
    return <div className={cn('h-full w-full bg-destructive', radius)} />;
  }

  if (status === 'done') {
    return <div className={cn('h-full w-full bg-emerald-500', radius)} />;
  }

  const green = Math.min(100, Math.max(0, donePct));
  const amber = Math.min(100 - green, Math.max(0, activePct));

  return (
    <div className={cn('flex h-full w-full overflow-hidden bg-muted', radius)}>
      <div className={'h-full bg-emerald-500 transition-all duration-300'} style={{ width: `${green}%` }} />
      <div className={'h-full bg-amber-500 transition-all duration-300'} style={{ width: `${amber}%` }} />
    </div>
  );
}

/**
 * Segmented progress: one tri-state bar per pair; greens up as it downloads,
 * fully green when done.
 */

function Segments({ items, jobDone }: { items: DownloadItem[]; jobDone: boolean }): JSX.Element {
  return (
    <div className={'flex h-2.5 w-full gap-1'}>
      {items.map(item => {
        const status = jobDone && item.status !== 'error' ? 'done' : item.status;
        const donePct = item.monthsTotal ? (item.monthsDone / item.monthsTotal) * 100 : 0;
        const activePct = item.monthsTotal ? (item.monthsActive / item.monthsTotal) * 100 : 0;

        return (
          <div className={'h-full flex-1'} key={item.symbol} title={item.symbol}>
            <DownloadBar activePct={activePct} donePct={donePct} rounded={'sm'} status={status} />
          </div>
        );
      })}
    </div>
  );
}

/**
 * Overall done / active percentages across items (done + active months / total).
 */

function overallPercents(items: DownloadItem[]): { donePct: number; activePct: number } {
  const total = items.reduce((acc, item) => acc + (item.monthsTotal || 0), 0);

  if (!total) {
    return { donePct: 0, activePct: 0 };
  }

  const done = items.reduce((acc, item) => acc + (item.status === 'done' ? item.monthsTotal : item.monthsDone), 0);
  const active = items.reduce((acc, item) => acc + (item.status === 'downloading' ? item.monthsActive : 0), 0);

  return { donePct: (done / total) * 100, activePct: (active / total) * 100 };
}

/**
 * Human-readable byte size.
 */

function humanSize(bytes: number): string {
  const units = ['B', 'KB', 'MB', 'GB'];
  let value = bytes;
  let unit = 0;

  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }

  return `${value.toFixed(unit === 0 ? 0 : 1)} ${units[unit]}`;
}

/**
 * Format a ms timestamp as YYYY-MM-DD for a date input.
 */

function toISODate(ms: number | null | undefined): string {
  if (!ms) {
    return '';
  }

  const date = new Date(ms);

  return Number.isNaN(date.getTime()) ? '' : date.toISOString().slice(0, 10);
}

/**
 * Format a ms timestamp as a short date.
 */

function shortDate(ms: number | null): string {
  if (!ms) {
    return '—';
  }

  const date = new Date(ms);

  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleDateString();
}

/**
 * A backtest result metric card.
 */

function Metric({ label, value, tone }: { label: string; value: string; tone?: number }): JSX.Element {
  return (
    <div className={'rounded-lg border border-border p-3'}>
      <div className={'text-[10px] uppercase tracking-wide text-muted-foreground'}>{label}</div>

      <div
        className={cn(
          'mt-1 text-base font-semibold tabular-nums',
          typeof tone === 'number' && tone > 0 && 'text-emerald-600',
          typeof tone === 'number' && tone < 0 && 'text-destructive'
        )}
      >
        {value}
      </div>
    </div>
  );
}

/**
 * Export `CryptoBotBacktest` — data-availability gate + downloader + runner.
 *
 * Works off the *saved* config (pairs, interval): checks which pairs have a
 * local candle file, offers to download the missing ones (with progress), and
 * only enables the run once everything is present. The run itself streams
 * progress while it computes.
 */

export function CryptoBotBacktest(): JSX.Element {
  const [translate] = useTranslation();
  const { error } = useToast();

  const { data, refetch } = useCryptoBotBacktestData();
  const startDownload = useStartBacktestDownload();
  const cancelDownload = useCancelBacktestDownload();
  const startRun = useStartBacktestRun();
  const cancelRun = useCancelBacktestRun();

  const [dlOpen, setDlOpen] = useState(false);
  const [dlMinimized, setDlMinimized] = useState(false);
  const [runOpen, setRunOpen] = useState(false);
  const [dlEnabled, setDlEnabled] = useState(false);
  const [runEnabled, setRunEnabled] = useState(false);
  const [pair, setPair] = useState('');
  const [startCapital, setStartCapital] = useState(() => {
    try {
      return window.localStorage.getItem('cryptoBot.backtest.startCapital') || '10000';
    } catch {
      return '10000';
    }
  });
  const [filesPair, setFilesPair] = useState<string | null>(null);
  const [fromDate, setFromDate] = useState(() => {
    try {
      return window.localStorage.getItem('cryptoBot.backtest.from') || '';
    } catch {
      return '';
    }
  });
  const [toDate, setToDate] = useState(() => {
    try {
      return window.localStorage.getItem('cryptoBot.backtest.to') || '';
    } catch {
      return '';
    }
  });
  const lastPairRef = useRef<string | null>(null);

  const download = useBacktestDownloadStatus(dlEnabled);
  const run = useBacktestRunStatus(runEnabled);
  const files = useCryptoBotPairFiles(filesPair);

  // Default the run pair to the first one with data.
  useEffect(() => {
    if (data && !pair) {
      const ready = data.pairs.find(item => item.hasData) ?? data.pairs[0];

      if (ready) {
        setPair(ready.baseAsset);
      }
    }
  }, [data, pair]);

  // Refresh availability once a download finishes.
  useEffect(() => {
    if (download.data && download.data.done && !download.data.running) {
      refetch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [download.data?.done, download.data?.running, refetch]);

  // Default the date window to the coverage (selected pair, or the overlapping
  // window across all pairs for multi-asset) — on first load keep persisted
  // dates, only filling blanks. Switching pair/mode resets both.
  useEffect(() => {
    if (!data) {
      return;
    }

    const multi = data.strategy === 'rebalance_multi';
    const key = multi ? '__multi__' : pair;
    let first: number | null | undefined;
    let last: number | null | undefined;

    if (multi) {
      const ready = data.pairs.filter(item => item.hasData);

      if (!ready.length) {
        return;
      }

      first = Math.max(...ready.map(item => item.firstAt ?? 0));
      last = Math.min(...ready.map(item => item.lastAt ?? 0));
    } else {
      const item = data.pairs.find(entry => entry.baseAsset === pair);

      first = item?.firstAt;
      last = item?.lastAt;
    }

    if (!first || !last) {
      return;
    }

    if (lastPairRef.current === null) {
      lastPairRef.current = key;

      if (!fromDate) {
        setFromDate(toISODate(first));
      }

      if (!toDate) {
        setToDate(toISODate(last));
      }

      return;
    }

    if (lastPairRef.current !== key) {
      lastPairRef.current = key;
      setFromDate(toISODate(first));
      setToDate(toISODate(last));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pair, data]);

  if (!data) {
    return (
      <div className={'flex items-center gap-2 text-sm text-muted-foreground'}>
        <Loader2 className={'h-4 w-4 animate-spin'} />
        {translate('cryptoBot.settings.backtestChecking')}
      </div>
    );
  }

  const isMulti = data.strategy === 'rebalance_multi';
  const selectedPair = data.pairs.find(item => item.baseAsset === pair);
  // Multi-asset uses every pair, so the run gates on all pairs being ready and
  // the date bounds are the overlapping window across pairs.
  const readyPairs = data.pairs.filter(item => item.hasData);
  const multiFirst = readyPairs.length ? Math.max(...readyPairs.map(item => item.firstAt ?? 0)) : null;
  const multiLast = readyPairs.length ? Math.min(...readyPairs.map(item => item.lastAt ?? 0)) : null;
  const boundFirst = isMulti ? multiFirst : selectedPair?.firstAt;
  const boundLast = isMulti ? multiLast : selectedPair?.lastAt;
  const canRun = isMulti ? data.ready : !!selectedPair?.hasData;

  const onDownload = async (baseAssets?: string[]) => {
    try {
      await startDownload.mutateAsync(baseAssets ? { baseAssets } : {});
      setDlEnabled(true);
      setDlMinimized(false);
      setDlOpen(true);
    } catch (err: any) {
      error({ message: err?.response?.data?.data?.message ?? 'Error' });
    }
  };

  const onMinimize = () => {
    setDlOpen(false);
    setDlMinimized(true);
  };

  const onRestore = () => {
    setDlMinimized(false);
    setDlOpen(true);
  };

  const onCancel = async () => {
    try {
      await cancelDownload.mutateAsync();
    } catch (err: any) {
      error({ message: err?.response?.data?.data?.message ?? 'Error' });
    }

    setDlOpen(false);
    setDlMinimized(false);
  };

  const onRun = async () => {
    if (!isMulti && !pair) {
      return;
    }

    try {
      window.localStorage.setItem('cryptoBot.backtest.startCapital', startCapital);
      window.localStorage.setItem('cryptoBot.backtest.from', fromDate);
      window.localStorage.setItem('cryptoBot.backtest.to', toDate);
    } catch {
      // ignore storage errors
    }

    try {
      await startRun.mutateAsync({
        baseAsset: pair,
        startEquity: Number(startCapital) || undefined,
        from: fromDate || undefined,
        to: toDate || undefined
      });
      setRunEnabled(true);
      setRunOpen(true);
    } catch (err: any) {
      error({ message: err?.response?.data?.data?.message ?? 'Error' });
    }
  };

  const result = run.data?.result;

  return (
    <div className={'space-y-4'}>
      <p className={'text-xs text-muted-foreground'}>
        {translate('cryptoBot.settings.backtestUsesSaved', { interval: data.interval, years: data.years })}
      </p>

      {/* Data availability per pair */}
      <div className={'overflow-hidden rounded-lg border border-border'}>
        <table className={'w-full border-collapse text-sm'}>
          <tbody className={'divide-y divide-border'}>
            {data.pairs.map(item => (
              <tr key={item.symbol}>
                <td className={'px-3 py-2 font-medium text-foreground'}>{item.symbol}</td>

                <td className={'px-3 py-2'}>
                  {item.hasData ? (
                    <Badge className={'gap-1'} variant={'default'}>
                      <CheckCircle2 className={'h-3 w-3'} />
                      {translate('cryptoBot.settings.backtestHasData')}
                    </Badge>
                  ) : (
                    <Badge className={'gap-1'} variant={'secondary'}>
                      <XCircle className={'h-3 w-3'} />
                      {translate('cryptoBot.settings.backtestNoData')}
                    </Badge>
                  )}
                </td>

                <td className={'px-3 py-2 text-right text-xs tabular-nums text-muted-foreground'}>
                  {item.hasData
                    ? `${(item.rows ?? 0).toLocaleString('en-US')} · ${shortDate(item.firstAt)} → ${shortDate(
                        item.lastAt
                      )}`
                    : '—'}
                </td>

                <td className={'px-3 py-2'}>
                  <div className={'flex items-center justify-end gap-1'}>
                    {item.hasData ? (
                      <Button
                        className={'h-7 gap-1 px-2 text-xs'}
                        onClick={() => setFilesPair(item.baseAsset)}
                        size={'sm'}
                        type={'button'}
                        variant={'ghost'}
                      >
                        <FolderOpen className={'h-3.5 w-3.5'} />
                        {translate('cryptoBot.settings.backtestFiles')}
                      </Button>
                    ) : null}

                    <Button
                      className={'h-7 gap-1 px-2 text-xs'}
                      disabled={startDownload.isLoading || download.data?.running}
                      onClick={() => onDownload([item.baseAsset])}
                      size={'sm'}
                      type={'button'}
                      variant={item.hasData ? 'ghost' : 'outline'}
                    >
                      <Download className={'h-3.5 w-3.5'} />
                      {item.hasData
                        ? translate('cryptoBot.settings.backtestRedownload')
                        : translate('cryptoBot.settings.backtestDownloadShort')}
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className={'flex flex-wrap items-end gap-2'}>
        {!data.ready ? (
          <Button
            disabled={startDownload.isLoading || download.data?.running}
            onClick={() => onDownload()}
            type={'button'}
            variant={'outline'}
          >
            <Download className={'h-4 w-4'} />
            {translate('cryptoBot.settings.backtestDownloadAll', { years: data.years })}
          </Button>
        ) : null}

        {isMulti ? (
          <div className={'min-w-[140px] flex-1'}>
            <label className={'text-[11px] font-medium text-muted-foreground'}>
              {translate('cryptoBot.settings.backtestPair')}
            </label>

            <div className={'rounded-md border border-border bg-muted/40 px-3 py-2 text-sm text-muted-foreground'}>
              {translate('cryptoBot.settings.backtestAllPairs', { count: readyPairs.length })}
            </div>
          </div>
        ) : (
          <div className={'min-w-[140px] flex-1'}>
            <label className={'text-[11px] font-medium text-muted-foreground'}>
              {translate('cryptoBot.settings.backtestPair')}
            </label>

            <Select onValueChange={setPair} value={pair || undefined}>
              <SelectTrigger className={'w-full'}>
                <SelectValue placeholder={translate('cryptoBot.settings.pairsSelect')} />
              </SelectTrigger>

              <SelectContent>
                {data.pairs.map(item => (
                  <SelectItem disabled={!item.hasData} key={item.symbol} value={item.baseAsset}>
                    {item.symbol}
                    {item.hasData ? '' : ` — ${translate('cryptoBot.settings.backtestNoData')}`}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        <div className={'w-28'}>
          <label className={'text-[11px] font-medium text-muted-foreground'}>
            {translate('cryptoBot.settings.backtestStartCapital')}
          </label>

          <Input onChange={e => setStartCapital(e.target.value)} type={'number'} value={startCapital} />
        </div>

        <div className={'w-36'}>
          <label className={'text-[11px] font-medium text-muted-foreground'}>
            {translate('cryptoBot.settings.backtestStartDate')}
          </label>

          <Input
            disabled={!canRun}
            max={toISODate(boundLast)}
            min={toISODate(boundFirst)}
            onChange={e => setFromDate(e.target.value)}
            type={'date'}
            value={fromDate}
          />
        </div>

        <div className={'w-36'}>
          <label className={'text-[11px] font-medium text-muted-foreground'}>
            {translate('cryptoBot.settings.backtestEndDate')}
          </label>

          <Input
            disabled={!canRun}
            max={toISODate(boundLast)}
            min={toISODate(boundFirst)}
            onChange={e => setToDate(e.target.value)}
            type={'date'}
            value={toDate}
          />
        </div>

        <Button disabled={!canRun || startRun.isLoading} onClick={onRun} type={'button'}>
          <Play className={'h-4 w-4'} />
          {translate('cryptoBot.settings.runBacktest')}
        </Button>
      </div>

      {/* Minimized download indicator (inline) */}
      {dlMinimized && download.data ? (
        <button
          className={
            'flex w-full items-center gap-3 rounded-lg border border-border bg-card px-3 py-2 text-left shadow-sm hover:bg-muted/40'
          }
          onClick={onRestore}
          type={'button'}
        >
          {download.data.running ? (
            <Loader2 className={'h-4 w-4 shrink-0 animate-spin text-amber-500'} />
          ) : (
            <CheckCircle2 className={'h-4 w-4 shrink-0 text-emerald-600'} />
          )}

          <div className={'min-w-0 flex-1'}>
            <div className={'text-xs font-medium text-foreground'}>
              {download.data.running
                ? translate('cryptoBot.settings.backtestDownloadingN', {
                    done: download.data.items.filter(entry => entry.status === 'done').length,
                    total: download.data.items.length
                  })
                : translate('cryptoBot.settings.backtestDownloadDone')}
            </div>

            <div className={'h-2'}>
              <DownloadBar
                activePct={overallPercents(download.data.items).activePct}
                donePct={overallPercents(download.data.items).donePct}
                status={download.data.running ? 'downloading' : 'done'}
              />
            </div>
          </div>

          <span className={'text-[11px] text-muted-foreground'}>{translate('cryptoBot.settings.backtestReopen')}</span>
        </button>
      ) : null}

      {/* Download progress modal */}
      <Dialog
        onOpenChange={open => {
          if (open) {
            setDlOpen(true);
          } else if (download.data?.running) {
            onMinimize();
          } else {
            setDlOpen(false);
          }
        }}
        open={dlOpen}
      >
        <DialogContent className={'max-w-lg'}>
          <DialogHeader>
            <DialogTitle>{translate('cryptoBot.settings.backtestDownloadTitle')}</DialogTitle>

            <DialogDescription>
              {translate('cryptoBot.settings.backtestDownloadDesc', { years: data.years, interval: data.interval })}
            </DialogDescription>
          </DialogHeader>

          <div className={'space-y-3'}>
            {(download.data?.items?.length ?? 0) > 1 ? (
              <div className={'space-y-1'}>
                <Segments items={download.data!.items} jobDone={!!download.data?.done} />

                <div className={'text-right text-[11px] tabular-nums text-muted-foreground'}>
                  {download.data!.items.filter(entry => entry.status === 'done').length}
                  {'/'}
                  {download.data!.items.length}
                </div>
              </div>
            ) : null}

            {(download.data?.items ?? []).map(item => {
              // Once the whole job is done, any non-errored item is complete —
              // guarantees the bar goes green even if the last poll raced.
              const status = download.data?.done && item.status !== 'error' ? 'done' : item.status;
              const donePct = item.monthsTotal ? (item.monthsDone / item.monthsTotal) * 100 : 0;
              const activePct = item.monthsTotal ? (item.monthsActive / item.monthsTotal) * 100 : 0;

              return (
                <div className={'space-y-1'} key={item.symbol}>
                  <div className={'flex items-center justify-between text-xs'}>
                    <span
                      className={cn(
                        'font-medium',
                        status === 'done' && 'text-emerald-600',
                        status === 'downloading' && 'text-amber-600',
                        status === 'error' && 'text-destructive',
                        status === 'pending' && 'text-muted-foreground'
                      )}
                    >
                      {item.symbol}
                    </span>

                    <span className={'tabular-nums text-muted-foreground'}>
                      {status === 'done'
                        ? `${item.candles.toLocaleString('en-US')} ${translate('cryptoBot.settings.backtestCandles')}`
                        : status === 'error'
                        ? item.error ?? 'error'
                        : status === 'pending'
                        ? translate('cryptoBot.settings.backtestQueued')
                        : `${item.monthsDone}/${item.monthsTotal || '…'}`}
                    </span>
                  </div>

                  <div className={'h-2'}>
                    <DownloadBar activePct={activePct} donePct={donePct} status={status} />
                  </div>
                </div>
              );
            })}

            {download.data?.done ? (
              <div className={'flex items-center gap-2 pt-1 text-sm text-emerald-600'}>
                <CheckCircle2 className={'h-4 w-4'} />
                {translate('cryptoBot.settings.backtestDownloadDone')}
              </div>
            ) : (
              <div className={'flex items-center gap-2 pt-1 text-sm text-muted-foreground'}>
                <Loader2 className={'h-4 w-4 animate-spin text-amber-500'} />
                {translate('cryptoBot.settings.backtestDownloadWait')}
              </div>
            )}
          </div>

          <div className={'flex justify-end gap-2 pt-2'}>
            {download.data?.running ? (
              <Button
                className={'text-destructive hover:text-destructive'}
                disabled={cancelDownload.isLoading}
                onClick={onCancel}
                type={'button'}
                variant={'ghost'}
              >
                <X className={'h-4 w-4'} />
                {translate('cryptoBot.settings.backtestCancel')}
              </Button>
            ) : null}

            <Button
              onClick={download.data?.running ? onMinimize : () => setDlOpen(false)}
              type={'button'}
              variant={'outline'}
            >
              {download.data?.running ? <Minimize2 className={'h-4 w-4'} /> : null}
              {download.data?.running
                ? translate('cryptoBot.settings.backtestMinimize')
                : translate('cryptoBot.settings.backtestClose')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Backtest progress modal */}
      <Dialog
        onOpenChange={open => {
          if (open) {
            setRunOpen(true);
          } else {
            if (run.data?.running) {
              cancelRun.mutate();
            }

            setRunOpen(false);
          }
        }}
        open={runOpen}
      >
        <DialogContent className={'max-w-lg'}>
          <DialogHeader>
            <DialogTitle>{translate('cryptoBot.settings.backtestRunTitle')}</DialogTitle>

            <DialogDescription>{run.data?.symbol ?? selectedPair?.symbol}</DialogDescription>
          </DialogHeader>

          {run.data?.error ? (
            <div className={'flex items-center gap-2 text-sm text-destructive'}>
              <XCircle className={'h-4 w-4'} />
              {run.data.error === 'empty-range'
                ? translate('cryptoBot.settings.backtestEmptyRange')
                : run.data.error === 'no-data'
                ? translate('cryptoBot.settings.backtestNoDataForPair')
                : run.data.error === 'cancelled'
                ? translate('cryptoBot.settings.backtestCancelled')
                : translate('cryptoBot.settings.backtestRunFailed', { error: run.data.error })}
            </div>
          ) : run.data && !run.data.done && run.data.phase === 'loading' ? (
            <div className={'flex items-center gap-2 py-4 text-sm text-muted-foreground'}>
              <Loader2 className={'h-4 w-4 animate-spin'} />
              {translate('cryptoBot.settings.backtestLoadingData')}
            </div>
          ) : run.data && !run.data.done ? (
            <div className={'space-y-2'}>
              <Bar value={run.data.progress} />

              <div className={'flex items-center justify-between text-xs tabular-nums text-muted-foreground'}>
                <span>
                  {run.data.progress}
                  {'%'}
                </span>

                <span>
                  {run.data.candlesDone.toLocaleString('en-US')}
                  {' / '}
                  {run.data.candlesTotal.toLocaleString('en-US')} {translate('cryptoBot.settings.backtestCandles')}
                </span>
              </div>
            </div>
          ) : result ? (
            <div className={'space-y-3'}>
              <div className={'flex items-center justify-between text-xs text-muted-foreground'}>
                <span>{shortDate(result.startAt)}</span>
                <span>{'→'}</span>
                <span>{shortDate(result.endAt)}</span>
              </div>

              {result.trades === 0 ? (
                <div
                  className={
                    'rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:border-amber-900/40 dark:bg-amber-950/30 dark:text-amber-200'
                  }
                >
                  {translate('cryptoBot.settings.backtestNoTrades')}
                </div>
              ) : null}

              <div className={'grid grid-cols-2 gap-3 sm:grid-cols-3'}>
                <Metric
                  label={translate('cryptoBot.settings.btReturn')}
                  tone={result.returnPct}
                  value={`${result.returnPct?.toFixed(2)}%`}
                />

                <Metric label={translate('cryptoBot.settings.btTrades')} value={String(result.trades)} />

                <Metric label={translate('cryptoBot.settings.btWinRate')} value={`${result.winRatePct?.toFixed(0)}%`} />

                <Metric
                  label={translate('cryptoBot.settings.btMaxDd')}
                  value={`${result.maxDrawdownPct?.toFixed(2)}%`}
                />

                <Metric
                  label={translate('cryptoBot.settings.btPnl')}
                  tone={result.pnl}
                  value={result.pnl?.toFixed(2)}
                />

                <Metric label={translate('cryptoBot.settings.btStartCapital')} value={result.startEquity?.toFixed(2)} />

                <Metric label={translate('cryptoBot.settings.btEndFiat')} value={result.endFiat?.toFixed(2)} />

                <Metric
                  label={translate('cryptoBot.settings.btEndCrypto')}
                  value={`$${(result.endCryptoValue ?? 0).toFixed(2)} (${(result.endQty ?? 0).toLocaleString('en-US', {
                    maximumFractionDigits: 6
                  })})`}
                />

                <Metric
                  label={translate('cryptoBot.settings.btFinalEquity')}
                  tone={result.pnl}
                  value={result.finalEquity?.toFixed(2)}
                />
              </div>

              {result.breakdown?.length ? (
                <div className={'rounded-lg border border-border p-3'}>
                  <div className={'mb-2 text-[10px] uppercase tracking-wide text-muted-foreground'}>
                    {translate('cryptoBot.settings.backtestBreakdown')}
                  </div>

                  <div className={'space-y-1'}>
                    {result.breakdown.map((b: any) => (
                      <div className={'flex items-center justify-between text-sm'} key={b.asset}>
                        <span className={'font-medium text-foreground'}>{b.asset}</span>

                        <span className={'tabular-nums text-muted-foreground'}>
                          {'$'}
                          {b.value.toFixed(2)}
                          {' ('}
                          {b.qty.toLocaleString('en-US', { maximumFractionDigits: 6 })}
                          {')'}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}
            </div>
          ) : (
            <div className={'flex items-center gap-2 text-sm text-muted-foreground'}>
              <Loader2 className={'h-4 w-4 animate-spin'} />
              {translate('cryptoBot.settings.backtestRunning')}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Downloaded files modal (per asset) */}
      <Dialog onOpenChange={open => (open ? undefined : setFilesPair(null))} open={!!filesPair}>
        <DialogContent className={'max-w-lg'}>
          <DialogHeader>
            <DialogTitle>{translate('cryptoBot.settings.backtestFilesTitle')}</DialogTitle>

            <DialogDescription>{files.data?.symbol ?? filesPair}</DialogDescription>
          </DialogHeader>

          {files.isLoading ? (
            <div className={'flex items-center gap-2 py-6 text-sm text-muted-foreground'}>
              <Loader2 className={'h-4 w-4 animate-spin'} />
              {translate('cryptoBot.settings.backtestChecking')}
            </div>
          ) : files.data && files.data.files.length ? (
            <div className={'overflow-hidden rounded-lg border border-border'}>
              <table className={'w-full border-collapse text-sm'}>
                <thead>
                  <tr
                    className={
                      'border-b border-border text-left text-[11px] uppercase tracking-wide text-muted-foreground'
                    }
                  >
                    <th className={'px-3 py-2 font-semibold'}>
                      {translate('cryptoBot.settings.backtestFileInterval')}
                    </th>
                    <th className={'px-3 py-2 text-right font-semibold'}>
                      {translate('cryptoBot.settings.backtestFileCandles')}
                    </th>
                    <th className={'px-3 py-2 text-right font-semibold'}>
                      {translate('cryptoBot.settings.backtestFileRange')}
                    </th>
                    <th className={'px-3 py-2 text-right font-semibold'}>
                      {translate('cryptoBot.settings.backtestFileSize')}
                    </th>
                  </tr>
                </thead>

                <tbody className={'divide-y divide-border'}>
                  {files.data.files.map(file => (
                    <tr key={file.interval}>
                      <td className={'px-3 py-2 font-medium text-foreground'}>{file.interval}</td>
                      <td className={'px-3 py-2 text-right tabular-nums'}>{file.rows.toLocaleString('en-US')}</td>
                      <td className={'px-3 py-2 text-right text-xs tabular-nums text-muted-foreground'}>
                        {shortDate(file.firstAt)}
                        {' → '}
                        {shortDate(file.lastAt)}
                      </td>
                      <td className={'px-3 py-2 text-right tabular-nums text-muted-foreground'}>
                        {humanSize(file.sizeBytes)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className={'py-6 text-center text-sm text-muted-foreground'}>
              {translate('cryptoBot.settings.backtestFilesEmpty')}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
